import { useTheme } from '@fluentui/react';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPNav, IPNavLink, IPNavRef, IPNavStyles } from 'baseComponents/Nav/IPNav';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  FocusEvent as ReactFocusEvent,
  forwardRef,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { toNavLink } from './navLinks';
import { buildNavRootSelectors, buildNavStyles, NavGroupCaption, navRailClass } from './navMainStyles';
import { INavModelItem } from './navModel';

//
// The callout beside a pinned group header: the group's name over its pages, drawn with the menu's
// own rows. It does not know the model; the rail hands it the header and hears back which page was
// picked.
//

export interface INavHintCalloutProps {
  /** The header row the callout is anchored to. A new element moves the open callout. */
  target: HTMLElement;
  /** The pinned group whose pages the callout lists. Its label is the overline. */
  header: INavModelItem;
  /** The current page, accented in the list. */
  selectedKey: string;
  /** Move keyboard focus into the list when the callout opens. */
  focusOnOpen: boolean;
  onSelect: (id: string) => void;
  /** Opens or closes an entry nested in the group, which expands in place inside the callout. */
  onToggleExpanded: (id: string) => void;
  onDismiss: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export interface INavHintCalloutHandle {
  /** Moves keyboard focus to the first page. */
  focus: () => void;
}

/**
 * Fluent closes a callout when a click or a focus change lands outside it. Inside the menu that is
 * the user working the menu, so those stay. Scrolling the menu moves the header away, so that one
 * closes it.
 */
const preventDismissInsideMenu = (ev: Event | ReactFocusEvent | ReactKeyboardEvent | ReactMouseEvent): boolean => {
  if (ev.type === 'scroll') {
    return false;
  }

  return ev.target instanceof Element && ev.target.closest(`.${navRailClass}`) !== null;
};

const NavHintCallout = forwardRef<INavHintCalloutHandle, INavHintCalloutProps>((props, ref) => {
  const theme = useTheme();
  const navRef = useRef<IPNavRef>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        navRef.current?.focus(true);
      },
    }),
    [],
  );

  // A focus request that arrives while the callout is already open. setInitialFocus below covers
  // the request that opens it.
  useEffect(() => {
    if (props.focusOnOpen) {
      navRef.current?.focus(true);
    }
  }, [props.focusOnOpen]);

  const navStyles = useMemo<Partial<IPNavStyles>>(
    () => ({
      ...buildNavStyles(theme, false),
      root: {
        boxSizing: 'border-box',
        // Fluent scrolls the menu itself; inside a callout that would be a second scrollbar.
        overflow: 'visible',
        selectors: buildNavRootSelectors(theme, false),
      },
      groupContent: {
        marginBottom: 6,
      },
    }),
    [theme],
  );

  const links = useMemo<IPNavLink[]>(
    () => (props.header.children ?? []).map((child) => toNavLink(child, { withLinks: true, hintable: false })),
    [props.header],
  );

  // A page inside a closed nested entry accents that entry, the nearest row on screen.
  const selectedKey = useMemo(() => {
    const closedParent = (props.header.children ?? []).find(
      (child) =>
        child.isExpanded !== true && (child.children ?? []).some((grandchild) => grandchild.id === props.selectedKey),
    );

    return closedParent?.id ?? props.selectedKey;
  }, [props.header, props.selectedKey]);

  const onLinkClick = (ev?: ReactMouseEvent<HTMLElement>, link?: IPNavLink): void => {
    // A nested entry has no page of its own: Fluent calls onLinkExpandClick next, which opens or
    // closes it.
    if (!link?.key || (link.links?.length ?? 0) > 0) {
      return;
    }

    props.onSelect(link.key);
  };

  const onLinkExpandClick = (ev?: ReactMouseEvent<HTMLElement>, link?: IPNavLink): void => {
    // Without this Fluent also flips its own copy of the flag.
    ev?.preventDefault();
    if (link?.key && (link.links?.length ?? 0) > 0) {
      props.onToggleExpanded(link.key);
    }
  };

  return (
    <IPCallout
      target={props.target}
      directionalHint={IPDirectionalHint.rightTopEdge}
      isBeakVisible={false}
      gapSpace={4}
      calloutMinWidth={200}
      calloutMaxWidth={280}
      setInitialFocus={props.focusOnOpen}
      onDismiss={props.onDismiss}
      preventDismissOnEvent={preventDismissInsideMenu}
    >
      <IPStack onMouseEnter={props.onMouseEnter} onMouseLeave={props.onMouseLeave}>
        <NavGroupCaption text={props.header.label} />
        <IPNav
          componentRef={navRef}
          selectedKey={selectedKey}
          styles={navStyles}
          groups={[{ links }]}
          onLinkClick={onLinkClick}
          onLinkExpandClick={onLinkExpandClick}
        />
      </IPStack>
    </IPCallout>
  );
});

NavHintCallout.displayName = 'NavHintCallout';

export default NavHintCallout;
